import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, Calendar, CheckCircle, AlertCircle, FileText, Clock, Video, User, Users, CreditCard, X, Download, TrendingUp, Receipt, Phone, Mail, Loader } from 'lucide-react';
import axios from 'axios';
import { getImageUrl } from '../utils/imageUtils';
import { getCardGradient, getCardInitials, CardThumbnail } from '../utils/cardUtils';

// ─── Payment History Modal ─────────────────────────────────────────────────────
const PaymentHistoryModal = ({ unit, user, onClose }) => {
  const navigate = useNavigate();
  const [historyData, setHistoryData] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await axios.get(`/payments/history/${user.id}/${unit.id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setHistoryData(res.data);
      } catch (err) {
        console.error('Error loading payment history:', err);
      } finally {
        setLoadingHistory(false);
      }
    };
    fetchHistory();
  }, [unit.id, user.id]);

  const downloadReceipt = () => {
    const enrollment = historyData?.enrollment;
    const payments = historyData?.payments || [];
    const totalPaid = parseFloat(enrollment?.paidAmount || 0);
    const totalFee = parseFloat(enrollment?.totalAmount || 0);
    const balance = totalFee - totalPaid;
    const unitInfo = historyData?.unit;
    const studentInfo = historyData?.student;

    const rows = payments.map((p, i) => `
      <tr style="background:${i % 2 === 0 ? '#f9fafb' : '#fff'}">
        <td style="padding:10px 12px;font-size:12px">${i + 1}</td>
        <td style="padding:10px 12px;font-size:12px">${new Date(p.createdAt).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
        <td style="padding:10px 12px;font-size:12px;text-transform:capitalize">${p.paymentMethod}</td>
        <td style="padding:10px 12px;font-size:12px">${p.referenceNumber || p.mpesaCode || '—'}</td>
        <td style="padding:10px 12px;font-size:12px;text-align:right;font-weight:700">KES ${parseFloat(p.amount).toLocaleString()}</td>
        <td style="padding:10px 12px;font-size:12px">
          <span style="padding:2px 8px;border-radius:9999px;font-size:11px;font-weight:700;background:${p.status==='confirmed'?'#dcfce7':p.status==='pending'?'#fef9c3':'#fee2e2'};color:${p.status==='confirmed'?'#166534':p.status==='pending'?'#854d0e':'#991b1b'}">${p.status}</span>
        </td>
      </tr>`).join('');

    const html = `<!DOCTYPE html><html><head><title>Payment Receipt — ${unitInfo?.name || unit.name}</title><meta charset="utf-8">
<style>body{font-family:system-ui,sans-serif;margin:0;padding:24px;color:#111}table{width:100%;border-collapse:collapse}th{text-align:left;padding:10px 12px;font-size:11px;text-transform:uppercase;letter-spacing:.05em;background:#1e1b4b;color:#fff}@media print{button{display:none}}</style></head><body>
<div style="max-width:700px;margin:0 auto">
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;padding-bottom:16px;border-bottom:2px solid #e5e7eb">
    <div><h1 style="margin:0;font-size:20px;color:#1e1b4b">${studentInfo?.institution?.name || 'INTUIT LMS'}</h1><p style="margin:4px 0 0;color:#6b7280;font-size:13px">${studentInfo?.campus?.name ? studentInfo.campus.name + ' Campus' : 'Payment Receipt'}</p></div>
    <div style="text-align:right"><p style="margin:0;font-size:12px;color:#6b7280">Issued: ${new Date().toLocaleDateString('en-KE',{day:'numeric',month:'long',year:'numeric'})}</p></div>
  </div>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:24px">
    <div><p style="margin:0;font-size:11px;color:#6b7280;text-transform:uppercase;font-weight:700">Student Details</p><p style="margin:4px 0 0;font-size:14px;font-weight:700">${studentInfo?.name || user.name || 'Student'}</p>${studentInfo?.admissionNumber ? `<p style="margin:2px 0 0;font-size:12px;color:#6b7280">ID: ${studentInfo.admissionNumber}</p>` : ''}</div>
    <div><p style="margin:0;font-size:11px;color:#6b7280;text-transform:uppercase;font-weight:700">Unit Info</p><p style="margin:4px 0 0;font-size:14px;font-weight:700">${unitInfo?.name || unit.name}</p>${unitInfo?.course?.name ? `<p style="margin:2px 0 0;font-size:12px;color:#6b7280">Course: ${unitInfo.course.name}</p>` : ''}</div>
  </div>
  <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-bottom:24px">
    <div style="background:#f0fdf4;padding:12px;border-radius:8px;text-align:center"><p style="margin:0;font-size:11px;color:#15803d;font-weight:700;text-transform:uppercase">Paid</p><p style="margin:4px 0 0;font-size:18px;font-weight:900;color:#166534">KES ${totalPaid.toLocaleString()}</p></div>
    <div style="background:#f9fafb;padding:12px;border-radius:8px;text-align:center"><p style="margin:0;font-size:11px;color:#6b7280;font-weight:700;text-transform:uppercase">Total</p><p style="margin:4px 0 0;font-size:18px;font-weight:900">KES ${totalFee.toLocaleString()}</p></div>
    <div style="background:${balance<=0?'#f0fdf4':'#fff7ed'};padding:12px;border-radius:8px;text-align:center"><p style="margin:0;font-size:11px;color:${balance<=0?'#15803d':'#c2410c'};font-weight:700;text-transform:uppercase">Balance</p><p style="margin:4px 0 0;font-size:18px;font-weight:900;color:${balance<=0?'#166534':'#9a3412'}">${balance<=0?'CLEARED':'KES '+balance.toLocaleString()}</p></div>
  </div>
  <table><thead><tr><th>#</th><th>Date</th><th>Method</th><th>Reference</th><th style="text-align:right">Amount</th><th>Status</th></tr></thead><tbody>${rows}</tbody></table>
  <div style="margin-top:24px;padding-top:16px;border-top:1px solid #e5e7eb;text-align:center;color:#9ca3af;font-size:11px">This is a computer-generated receipt. For queries, contact your institution.</div>
</div>
<script>window.print();</script></body></html>`;

    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
  };

  const enrollment = historyData?.enrollment;
  const payments = historyData?.payments || [];
  const totalPaid = parseFloat(enrollment?.paidAmount || 0);
  const totalFee = parseFloat(enrollment?.totalAmount || 0);
  const balance = totalFee - totalPaid;
  const progressPct = totalFee > 0 ? Math.min((totalPaid / totalFee) * 100, 100) : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-700 p-5 flex items-start justify-between">
          <div>
            <p className="text-indigo-200 text-xs font-bold uppercase tracking-widest mb-1">Payment Details</p>
            <h2 className="text-white font-black text-lg leading-tight">{unit.name}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loadingHistory ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Summary */}
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center p-3 bg-gray-50 rounded-xl">
                  <p className="text-[10px] text-gray-500 uppercase font-bold tracking-widest mb-1">Total Fee</p>
                  <p className="text-sm font-black text-gray-900">KES {totalFee.toLocaleString()}</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-xl">
                  <p className="text-[10px] text-green-600 uppercase font-bold tracking-widest mb-1">Paid</p>
                  <p className="text-sm font-black text-green-700">KES {totalPaid.toLocaleString()}</p>
                </div>
                <div className={`text-center p-3 rounded-xl ${balance <= 0 ? 'bg-green-50' : 'bg-orange-50'}`}>
                  <p className={`text-[10px] uppercase font-bold tracking-widest mb-1 ${balance <= 0 ? 'text-green-600' : 'text-orange-600'}`}>Balance</p>
                  <p className={`text-sm font-black ${balance <= 0 ? 'text-green-700' : 'text-orange-700'}`}>
                    {balance <= 0 ? '✓ CLEARED' : `KES ${balance.toLocaleString()}`}
                  </p>
                </div>
              </div>

              {/* Progress */}
              <div>
                <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-indigo-500 to-green-500 rounded-full transition-all duration-700" style={{ width: `${progressPct}%` }} />
                </div>
                <p className="text-[10px] text-gray-400 mt-1">{progressPct.toFixed(0)}% paid</p>
              </div>

              {/* Payments List */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Receipt className="w-4 h-4 text-gray-500" />
                  <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">Transaction History</span>
                </div>
                {payments.length === 0 ? (
                  <div className="text-center py-6 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                    <CreditCard className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No payments recorded yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {payments.map((p, i) => (
                      <div key={p.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-black text-indigo-600">{i + 1}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-bold text-gray-800 capitalize">{p.paymentMethod} Payment</p>
                            <p className="text-sm font-black text-gray-900">KES {parseFloat(p.amount).toLocaleString()}</p>
                          </div>
                          <div className="flex items-center justify-between mt-0.5">
                            <p className="text-[11px] text-gray-500">{new Date(p.createdAt).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                              p.status === 'confirmed' ? 'bg-green-100 text-green-700'
                              : p.status === 'rejected' ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-700'
                            }`}>{p.status}</span>
                          </div>
                          {(p.referenceNumber || p.mpesaCode) && (
                            <p className="text-[11px] text-gray-400 mt-0.5 font-mono">Ref: {p.referenceNumber || p.mpesaCode}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 flex gap-2">
          {balance > 0 && (
            <button
              onClick={() => { onClose(); navigate(`/payment/${unit.id}`); }}
              className="flex-1 py-2.5 bg-indigo-600 text-white rounded-xl font-bold text-sm hover:bg-indigo-700 transition-colors"
            >
              💳 Pay Balance (KES {balance.toLocaleString()})
            </button>
          )}
          <button
            onClick={downloadReceipt}
            disabled={loadingHistory || payments.length === 0}
            className="flex items-center gap-1.5 px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-bold text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40"
          >
            <Download className="w-4 h-4" /> Receipt
          </button>
          {balance <= 0 && <button onClick={onClose} className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-bold text-sm hover:bg-gray-200 transition-colors">Close</button>}
        </div>
      </div>
    </div>
  );
};

const Units = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [units, setUnits] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(state?.fromCourse || null);
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(state?.fromFilter || 'all');
  const [downloading, setDownloading] = useState(false);
  const [paymentModalUnit, setPaymentModalUnit] = useState(null);

  useEffect(() => {
    fetchUnits();
  }, []);

  const fetchUnits = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔍 Fetching units for student ID:', user.id);

      // First, get student details to get courseId and campusId
      const studentResponse = await axios.get(`/students/${user.id}?includeCourse=true&includeCampus=true`);
      const studentData = studentResponse.data;
      setStudentData(studentData);
      console.log('✅ Student data loaded:', studentData);

      // Fetch available courses from API
      console.log('🌐 Fetching available courses from API...');
      const availableCoursesResponse = await axios.get(`/enrollments/available-courses?studentId=${user.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('✅ Available courses API response:', availableCoursesResponse.data);
      const availableCoursesData = availableCoursesResponse.data || [];
      setCourses(availableCoursesData);

      // Handle self-paced vs instructor-led logic
      const isSelfPaced = studentData.institution?.learningMode === 'self_paced' ||
        availableCoursesData.some(c => c.selfPacedEnabled);

      if (!studentData.courseId && !isSelfPaced) {
        console.log('❌ Student has no courseId and is not self-paced');
        setUnits([]);
        return;
      }

      // Flatten all units from all available courses for initial state/stats
      // But we'll filter them by selectedCourse if in self-paced mode
      const courseUnitsData = [];
      availableCoursesData.forEach((course) => {
        if (course.Units) {
          courseUnitsData.push(...course.Units.map(unit => ({
            ...unit,
            courseName: course.name,
            courseId: course.id,
            selfPacedEnabled: course.selfPacedEnabled || studentData.institution?.learningMode === 'self_paced'
          })));
        }
      });

      console.log(`📋 Total flattened units: ${courseUnitsData.length}`);

      // Fetch cost and enrollment info (Keep existing logic)
      // ... (merged logic simplified below for brevity in replace)

      // Get student's enrolled units
      const studentUnitsResponse = await axios.get('/student-units');
      const studentUnitsData = studentUnitsResponse.data || [];
      const studentUnitMap = new Map(studentUnitsData.map(su => [su.unitId, su]));

      const processedUnits = courseUnitsData.map((unit) => {
        const studentUnit = studentUnitMap.get(unit.id);

        return {
          ...unit,
          cost: unit.cost || null,
          currency: unit.currency || 'KES',
          isFree: unit.isFree ?? studentUnit?.unit?.isFree ?? false,
          freeDescription: unit.freeDescription || studentUnit?.unit?.freeDescription || null,
          originalStatus: unit.status,
          enrollmentStatus: studentUnit ? studentUnit.status : 'not_enrolled',
          paymentStatus: studentUnit ? studentUnit.paymentStatus : 'not_enrolled',
          studentUnitId: studentUnit ? studentUnit.id : null,
          progress: studentUnit ? calculateProgress(studentUnit.status) : 0,
          hasLiveLesson: studentUnit?.hasLiveLesson || false,
          liveLessonTitle: studentUnit?.liveLessonTitle || null,
          hasActiveVirtualClass: studentUnit?.hasActiveVirtualClass || false,
          currentCohortStatus: studentUnit?.unit?.currentCohortStatus,
          minClassSize: studentUnit?.unit?.minClassSize,
          enrolledCount: studentUnit?.unit?.enrolledCount,
          cohortProgress: studentUnit?.unit?.cohortProgress,
          // Cohort status fields from enriched API response
          cohortStatus: studentUnit?.cohortStatus || null,
          cohortRun: studentUnit?.cohortRun || null,
          cohortLecturer: studentUnit?.cohortLecturer || null,
          Lecturer: studentUnit?.AssignedTutor || studentUnit?.unit?.lecturer || unit.Lecturer || unit.lecturer
        };
      });

      setUnits(processedUnits);
    } catch (error) {
      console.error('Error fetching units:', error);
      setError('Failed to load units. Please try again.');
      setUnits([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = (status) => {
    switch (status) {
      case 'completed': return 100;
      case 'in-progress': return 75;
      case 'active': return 50;
      default: return 0;
    }
  };

  const handleEnrollUnit = async (unitId, unitName) => {
    try {
      const response = await axios.post('/enrollments/enroll-unit', {
        studentId: user.id,
        unitId
      });

      if (response.data) {
        const enrollment = response.data.enrollment;
        if (enrollment && enrollment.paymentStatus === 'confirmed') {
          // Free unit auto-confirmed
          alert(`Successfully enrolled in ${unitName}. This is a free course!`);
          // Refresh list or navigate to unit
          fetchUnits();
        } else {
          // Enrollment successful - navigate to payment
          alert(`Successfully enrolled in ${unitName}. Proceeding to payment...`);
          navigate(`/payment/${unitId}`);
        }
      } else {
        alert('Failed to enroll in unit');
      }
    } catch (error) {
      console.error('Error enrolling in unit:', error);
      alert('Failed to enroll in unit. Please try again.');
    }
  };

  const handleDownloadTranscript = async () => {
    try {
      setDownloading(true);
      const response = await axios.get(`/students/${user.id}/transcript`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transcript_${user.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading transcript:', error);
      alert('Failed to download transcript. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadUnitTranscript = async (unitId, unitName) => {
    try {
      setDownloading(true);
      const response = await axios.get(`/student-units/${unitId}/unit-transcript`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transcript_${unitName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading unit transcript:', error);
      alert('Failed to download unit transcript. Please try again.');
    } finally {
      setDownloading(false);
    }
  };
  const handleRetakeUnit = async (studentUnitId, unitId) => {
    try {
      setLoading(true);
      await axios.post(`/student-units/${user.id}/${unitId}/retake`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      alert('Unit reset successfully! You can now enroll again.');
      fetchUnits(); // Refresh the list
    } catch (error) {
      console.error('Error resetting unit:', error);
      alert(error.response?.data?.message || 'Failed to reset unit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredUnits = units.filter(unit => {
    // If a course is selected (drill-down), only show units for that course
    if (selectedCourse && unit.courseId !== selectedCourse.id) return false;

    if (filter === 'all') return true;
    if (filter === 'active') return unit.status === 'active';
    if (filter === 'completed') return unit.status === 'completed';
    if (filter === 'upcoming') return unit.status === 'upcoming';
    if (filter === 'pending') return unit.paymentStatus === 'pending';
    return true;
  });

  const isSelfPacedMode = studentData?.institution?.learningMode === 'self_paced' || studentData?.course?.selfPacedEnabled;

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'completed': return 'text-blue-600 bg-blue-100';
      case 'upcoming': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-green-600 bg-green-100';
      case 'partial': return 'text-blue-600 bg-blue-100';
      case 'pending': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'in-progress': return 'In Progress';
      case 'active': return 'Active';
      case 'upcoming': return 'Upcoming';
      default: return 'Pending';
    }
  };

  // ─── CohortStatusBanner — shown inside a paid UnitCard ──────────────────────
  // Handles both self-paced (currentCohortStatus + Lecturer) and
  // instructor-led (cohortStatus + cohortLecturer) units.
  const CohortStatusBanner = ({ unit }) => {
    if (unit.enrollmentStatus === 'completed') return null;

    // ── Helper: render the tutor/lecturer contact row ──
    const TutorRow = ({ info, scheme }) => {
      if (!info) return null;
      const border = scheme === 'teal' ? 'border-teal-200' : 'border-green-200';
      const bg     = scheme === 'teal' ? 'bg-teal-200'    : 'bg-green-200';
      const icon   = scheme === 'teal' ? 'text-teal-700'  : 'text-green-700';
      const name   = scheme === 'teal' ? 'text-teal-900'  : 'text-green-900';
      const link   = scheme === 'teal' ? 'text-teal-700 hover:text-teal-900' : 'text-green-700 hover:text-green-900';
      return (
        <div className="flex items-center gap-2 mb-2" onClick={e => e.stopPropagation()}>
          {info.profilePicture ? (
            <img src={info.profilePicture} alt={info.name} className={`w-7 h-7 rounded-full object-cover border-2 ${border}`} />
          ) : (
            <div className={`w-7 h-7 rounded-full ${bg} flex items-center justify-center`}>
              <User className={`w-3.5 h-3.5 ${icon}`} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className={`text-xs font-bold ${name} truncate`}>{info.name}</p>
            <div className="flex flex-wrap gap-2 mt-0.5">
              {info.phoneNumber && (
                <a href={`tel:${info.phoneNumber}`} className={`flex items-center gap-0.5 text-[10px] ${link} font-bold`}>
                  <Phone className="w-2.5 h-2.5" />{info.phoneNumber}
                </a>
              )}
              {info.email && (
                <a href={`mailto:${info.email}`} className={`flex items-center gap-0.5 text-[10px] ${link} font-bold`}>
                  <Mail className="w-2.5 h-2.5" />{info.email}
                </a>
              )}
            </div>
          </div>
        </div>
      );
    };

    // ─── SELF-PACED PATH ──────────────────────────────────────────
    if (unit.selfPacedEnabled) {
      // Prioritize explicit active cohort status if assigned
      const spStatus = unit.cohortStatus === 'active' ? 'active' : unit.currentCohortStatus;
      const tutorInfo = unit.Lecturer || unit.lecturer;

      // If no cohort status set yet — show 'pending' awaiting state
      if (!spStatus) {
        return (
          <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <Loader className="w-3.5 h-3.5 text-amber-600 animate-spin" />
              <span className="text-xs font-black text-amber-800 uppercase tracking-wide">Awaiting Class Formation</span>
            </div>
            <p className="text-[11px] text-amber-700 leading-relaxed">
              Your payment is received. Your tutor is gathering students — class will start once enough students have enrolled.
            </p>
          </div>
        );
      }

      if (spStatus === 'forming') {
        const filled = unit.enrolledCount || 0;
        const max    = unit.minClassSize  || 1;
        const pct    = Math.min(100, Math.round((filled / max) * 100));
        return (
          <div className="mb-3 rounded-xl border border-teal-200 bg-teal-50 p-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500" />
              </span>
              <span className="text-xs font-black text-teal-800 uppercase tracking-wide">Class is Forming!</span>
            </div>
            <TutorRow info={tutorInfo} scheme="teal" />
            <div className="mt-1">
              <div className="flex justify-between text-[10px] text-teal-700 font-bold mb-1">
                <span>{filled} / {max} students enrolled</span>
                <span>{pct}% full</span>
              </div>
              <div className="w-full bg-teal-100 rounded-full h-1.5">
                <div className="h-1.5 rounded-full bg-teal-500 transition-all" style={{ width: `${pct}%` }} />
              </div>
              <p className="text-[10px] text-teal-600 mt-1">Class starts once minimum class size is reached.</p>
            </div>
          </div>
        );
      }

      if (spStatus === 'active') {
        return (
          <div className="mb-3 rounded-xl border border-green-200 bg-green-50 p-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-3.5 h-3.5 text-green-600" />
              <span className="text-xs font-black text-green-800 uppercase tracking-wide">Class is Active ✓</span>
            </div>
            <TutorRow info={tutorInfo} scheme="green" />
          </div>
        );
      }

      return null;
    }

    // ─── INSTRUCTOR-LED PATH ─────────────────────────────────────
    const { cohortStatus, cohortRun, cohortLecturer } = unit;
    if (!cohortStatus || !['pending', 'forming', 'active'].includes(cohortStatus)) return null;

    if (cohortStatus === 'pending') {
      return (
        <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3" onClick={e => e.stopPropagation()}>
          <div className="flex items-center gap-2 mb-1">
            <Loader className="w-3.5 h-3.5 text-amber-600 animate-spin" />
            <span className="text-xs font-black text-amber-800 uppercase tracking-wide">Awaiting Class Formation</span>
          </div>
          <p className="text-[11px] text-amber-700 leading-relaxed">
            Your payment is confirmed. Your lecturer is yet to open a class for this unit — you'll be placed into a cohort as soon as one is created.
          </p>
        </div>
      );
    }

    if (cohortStatus === 'forming') {
      const filled = cohortRun?.studentCount || 0;
      const max    = cohortRun?.maxSize      || 1;
      const pct    = Math.min(100, Math.round((filled / max) * 100));
      return (
        <div className="mb-3 rounded-xl border border-teal-200 bg-teal-50 p-3" onClick={e => e.stopPropagation()}>
          <div className="flex items-center gap-2 mb-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500" />
            </span>
            <span className="text-xs font-black text-teal-800 uppercase tracking-wide">Class is Forming!</span>
          </div>
          <TutorRow info={cohortLecturer} scheme="teal" />
          <div className="mt-1">
            <div className="flex justify-between text-[10px] text-teal-700 font-bold mb-1">
              <span>{filled} / {max} students enrolled</span>
              <span>{pct}% full</span>
            </div>
            <div className="w-full bg-teal-100 rounded-full h-1.5">
              <div className="h-1.5 rounded-full bg-teal-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
            <p className="text-[10px] text-teal-600 mt-1">Class starts when the lecturer decides or it reaches capacity.</p>
          </div>
        </div>
      );
    }

    if (cohortStatus === 'active') {
      return (
        <div className="mb-3 rounded-xl border border-green-200 bg-green-50 p-3" onClick={e => e.stopPropagation()}>
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-3.5 h-3.5 text-green-600" />
            <span className="text-xs font-black text-green-800 uppercase tracking-wide">Class is Active ✓</span>
          </div>
          <TutorRow info={cohortLecturer} scheme="green" />
        </div>
      );
    }

    return null;
  };

  const UnitCard = ({ unit }) => {
    let displayStatus = unit.originalStatus;
    let statusColor = getStatusColor(displayStatus);
    let statusText = getStatusText(displayStatus);
    let cardOpacity = '';

    if (unit.enrollmentStatus === 'not_enrolled') {
      if (unit.originalStatus === 'upcoming' || unit.originalStatus === 'active') {
        statusText = 'Available';
        statusColor = 'bg-blue-600 text-white';
      } else if (unit.originalStatus === 'pending') {
        statusText = 'Coming Soon';
        statusColor = 'bg-gray-200 text-gray-600';
        cardOpacity = 'opacity-70';
      } else {
        statusText = 'Not Available';
        statusColor = 'bg-gray-200 text-gray-500';
        cardOpacity = 'opacity-60';
      }
    } else if (unit.paymentStatus === 'pending' && !unit.isFree) {
      statusText = 'Payment Required';
      statusColor = 'bg-orange-500 text-white';
    } else if (unit.paymentStatus === 'partial' && !unit.isFree) {
      statusText = 'Partial Payment';
      statusColor = 'bg-blue-500 text-white';
    } else if (unit.isFree && unit.enrollmentStatus !== 'not_enrolled') {
      statusText = 'Enrolled — Free';
      statusColor = 'bg-indigo-600 text-white';
    } else if (unit.paymentStatus === 'confirmed') {
      statusText = 'Enrolled';
      statusColor = 'bg-green-600 text-white';
    }

    const handleCardClick = () => {
      if (unit.selfPacedEnabled) {
        navigate(`/self-paced/unit/${unit.id}`, { state: { fromCourse: selectedCourse, fromFilter: filter } });
        return;
      }
      if (unit.enrollmentStatus === 'completed') return;
      if (unit.enrollmentStatus !== 'not_enrolled' && unit.paymentStatus === 'pending' && !unit.isFree) return;
      navigate(`/units/${unit.id}`, { state: { unit, fromCourse: selectedCourse, fromFilter: filter } });
    };

    const { from, to } = getCardGradient(unit.name);
    const initials = getCardInitials(unit.name);
    const lecturerInfo = unit.Lecturer || unit.lecturer;
    const lecturerName = lecturerInfo?.name;

    return (
      <div
        className={`group cursor-pointer rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col ${cardOpacity}`}
        onClick={handleCardClick}
      >
        {/* Live/Virtual Notification — Top of Card */}
        {unit.hasLiveLesson ? (
          <div className="bg-gradient-to-r from-red-600 via-orange-500 to-red-600 bg-[length:200%_auto] animate-gradient-x text-white px-3 py-1.5 flex items-center justify-between shadow-sm z-20">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
              </span>
              <span className="text-[10px] font-black uppercase tracking-wider">Live Class in Progress</span>
            </div>
            <div className="bg-white/20 px-1.5 py-0.5 rounded text-[9px] font-bold backdrop-blur-sm">JOIN NOW</div>
          </div>
        ) : unit.hasActiveVirtualClass ? (
          <div className="bg-gradient-to-r from-indigo-600 to-blue-500 text-white px-3 py-1.5 flex items-center gap-2 shadow-sm z-20">
            <Video className="w-3.5 h-3.5 animate-pulse text-orange-400" />
            <span className="text-[10px] font-black uppercase tracking-wider">Virtual Classroom Active</span>
          </div>
        ) : null}

        {/* Thumbnail */}
        <div className="relative">
          <CardThumbnail name={unit.name} imageUrl={getImageUrl(unit.imageUrl)} />
          
          {/* Status chip — bottom left */}
          <div className="absolute bottom-3 left-3">
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full shadow-sm ${statusColor}`}>
              {unit.selfPacedEnabled && statusText === 'Available' ? 'Start Anytime' : statusText}
            </span>
          </div>

          {/* Self-paced chip — top right */}
          {unit.selfPacedEnabled && (
            <div className="absolute top-3 right-3">
              <span className="bg-purple-600 text-white text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider shadow">
                Self-Paced
              </span>
            </div>
          )}

          {/* Retake chip — bottom right */}
          {unit.isRetake && (
            <div className="absolute bottom-3 right-3">
              <span className="bg-orange-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider shadow">
                🔄 Retake
              </span>
            </div>
          )}
        </div>

        {/* Card Body */}
        <div className="p-4 flex flex-col flex-1">
          <h3 className="font-bold text-gray-900 text-sm leading-snug line-clamp-2 mb-1 group-hover:text-indigo-700 transition-colors">
            {unit.name}
          </h3>
          <div className="flex items-center gap-2 mb-2">
            {unit.selfPacedEnabled ? (
              <div className="w-6 h-6 rounded-full bg-indigo-50 flex items-center justify-center border border-indigo-100">
                <Users className="w-3 h-3 text-indigo-500" />
              </div>
            ) : lecturerInfo?.profilePicture ? (
              <img
                src={getImageUrl(lecturerInfo.profilePicture)}
                alt={lecturerName}
                className="w-6 h-6 rounded-full object-cover border border-gray-100"
              />
            ) : (
              <div className="w-6 h-6 rounded-full bg-gray-50 flex items-center justify-center border border-gray-200">
                <User className="w-3 h-3 text-gray-400" />
              </div>
            )}
            <p className="text-xs text-gray-500 font-medium truncate">
              {unit.selfPacedEnabled
                ? 'Expert Tutors'
                : lecturerName || <span className="text-orange-500 italic">Lecturer TBA</span>}
            </p>
          </div>
          {unit.description && (
            <p className="text-xs text-gray-600 line-clamp-3 mb-4 leading-relaxed">
              {unit.description}
            </p>
          )}
          {unit.code && (
            <p className="text-[10px] text-gray-400 mb-3 font-mono uppercase tracking-widest">{unit.code}</p>
          )}

          {/* Progress bar (enrolled + paid) */}
          {unit.enrollmentStatus !== 'not_enrolled' && unit.paymentStatus === 'confirmed' && (
            <div className="mb-3">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Progress</span>
                <span className="font-bold text-indigo-600">{unit.progress}%</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1.5">
                <div
                  className="bg-gradient-to-r from-indigo-500 to-purple-500 h-1.5 rounded-full transition-all duration-500"
                  style={{ width: `${unit.progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Partial payment indicator */}
          {unit.paymentStatus === 'partial' && (
            <div className="mb-3 flex items-center justify-between gap-2 bg-blue-50 rounded-lg px-3 py-2 text-xs border border-blue-100">
              <div className="flex items-center gap-1.5 text-blue-800 font-bold">
                <TrendingUp className="w-3.5 h-3.5" />
                Partial payment recorded
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setPaymentModalUnit(unit); }}
                className="text-blue-600 font-bold underline hover:text-blue-800"
              >
                Details
              </button>
            </div>
          )}

          {/* Completed banner */}
          {unit.enrollmentStatus === 'completed' && (
            <div className="mb-3 flex items-center gap-1.5 text-green-700 bg-green-50 rounded-lg px-3 py-1.5 text-xs font-bold">
              <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
              Unit Completed 🎉
            </div>
          )}

          {/* Availability message */}
          {unit.enrollmentStatus === 'not_enrolled' && !unit.isAvailableForEnrollment && unit.availabilityMessage && (
            <p className="text-[10px] text-yellow-700 bg-yellow-50 rounded px-2 py-1 mb-2">{unit.availabilityMessage}</p>
          )}

          {/* ── Cohort Status Banner — for enrolled+paid students (all unit types) ── */}
          {unit.enrollmentStatus !== 'not_enrolled' && (unit.paymentStatus === 'confirmed' || unit.paymentStatus === 'partial') && (
            <CohortStatusBanner unit={unit} />
          )}

          {/* Price + CTA */}
          <div className="mt-auto pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between mb-2.5">
              <span className={`font-black text-base ${unit.isFree || unit.currency === 'FREE' || unit.currency === 'FRE' ? 'text-indigo-600' : 'text-gray-900'}`}>
                {unit.isFree || unit.currency === 'FREE' || unit.currency === 'FRE' ? 'FREE' : unit.cost ? `${unit.currency} ${unit.cost.toLocaleString()}` : 'TBD'}
              </span>
              {unit.classDate && !unit.selfPacedEnabled && (
                <span className="text-[10px] text-gray-400 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(unit.classDate).toLocaleDateString()}
                </span>
              )}
            </div>

            <div className="flex gap-1.5">
              {unit.enrollmentStatus === 'completed' && (
                <button
                  onClick={(e) => { e.stopPropagation(); if (window.confirm('Retake this unit? This resets your progress and requires re-payment.')) handleRetakeUnit(unit.studentUnitId, unit.id); }}
                  className="flex-1 text-xs font-bold py-2 rounded-lg border-2 border-indigo-200 text-indigo-600 hover:bg-indigo-50 transition-colors"
                >
                  🔄 Retake
                </button>
              )}
              {unit.enrollmentStatus === 'not_enrolled' && unit.isAvailableForEnrollment && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleEnrollUnit(unit.id, unit.name); }}
                  className="flex-1 text-xs font-black py-2.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                >
                  {unit.selfPacedEnabled ? 'Start Learning →' : 'Enroll Now'}
                </button>
              )}
              {unit.enrollmentStatus !== 'not_enrolled' && unit.enrollmentStatus !== 'completed' && unit.paymentStatus === 'pending' && !unit.isFree && (
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`/payment/${unit.id}`); }}
                  className="flex-1 text-xs font-black py-2.5 rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors"
                >
                  💳 Pay Now
                </button>
              )}
              {unit.enrollmentStatus !== 'not_enrolled' && unit.paymentStatus === 'partial' && (
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`/payment/${unit.id}`); }}
                  className="flex-1 text-xs font-black py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                >
                  💳 Pay Balance
                </button>
              )}
              {((unit.enrollmentStatus === 'active' || unit.enrollmentStatus === 'enrolled') && (unit.paymentStatus === 'confirmed' || unit.isFree)) && (
                <button
                  onClick={(e) => { e.stopPropagation(); unit.selfPacedEnabled ? navigate(`/self-paced/unit/${unit.id}`) : navigate(`/units/${unit.id}`, { state: { unit, fromCourse: selectedCourse, fromFilter: filter } }); }}
                  className="flex-1 text-xs font-black py-2.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors flex items-center justify-center gap-1"
                >
                  Continue →
                </button>
              )}
              {unit.enrollmentStatus === 'not_enrolled' && !unit.isAvailableForEnrollment && (
                <button disabled className="flex-1 text-xs font-bold py-2.5 rounded-lg bg-gray-100 text-gray-400 cursor-not-allowed">
                  {unit.availabilityStatus === 'no_lecturer' ? 'No Lecturer'
                    : unit.availabilityStatus === 'unit_inactive' ? 'Not Active'
                    : 'Not Available'}
                </button>
              )}
              {(unit.enrollmentStatus === 'completed' || (unit.history && unit.history.length > 0)) && unit.paymentStatus === 'confirmed' && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleDownloadUnitTranscript(unit.id, unit.name); }}
                  disabled={downloading}
                  className="text-xs font-bold py-2 px-3 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors flex items-center"
                  title="Download Transcript"
                >
                  <FileText className="w-3.5 h-3.5" />
                </button>
              )}
              {unit.enrollmentStatus !== 'not_enrolled' && (unit.paymentStatus === 'confirmed' || unit.paymentStatus === 'partial') && (
                <button
                  onClick={(e) => { e.stopPropagation(); setPaymentModalUnit(unit); }}
                  className="text-xs font-bold py-2 px-3 rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-50 transition-colors flex items-center gap-1"
                  title="Payment Details"
                >
                  <Receipt className="w-3.5 h-3.5" />
                </button>
              )}


            </div>
          </div>
        </div>
      </div>
    );
  };

  const CourseCard = ({ course }) => {
    const enrolledUnitsCount = units.filter(u => u.courseId === course.id && u.enrollmentStatus !== 'not_enrolled').length;
    const totalUnitsCount = course.Units?.length || 0;
    const progress = totalUnitsCount > 0 ? (enrolledUnitsCount / totalUnitsCount) * 100 : 0;
    const { from, to } = getCardGradient(course.name);
    const initials = getCardInitials(course.name);

    return (
      <div
        onClick={() => setSelectedCourse(course)}
        className="group cursor-pointer rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col border border-gray-100"
      >
        {/* Thumbnail */}
        <div className="relative">
          <CardThumbnail name={course.name} imageUrl={getImageUrl(course.imageUrl)} />
          <div className="absolute top-3 right-3">
             <BookOpen className="w-5 h-5 text-white/50" />
          </div>
        </div>

        <div className="p-4 flex flex-col flex-1">
          <h3 className="font-bold text-gray-900 text-sm leading-snug line-clamp-2 mb-2 group-hover:text-indigo-700 transition-colors">{course.name}</h3>
          <p className="text-xs text-gray-500 line-clamp-2 mb-4 flex-1">{course.description}</p>

          <div className="mt-auto">
            <div className="flex justify-between text-[10px] text-gray-500 mb-1">
              <span>{enrolledUnitsCount} / {totalUnitsCount} Units</span>
              <span className="font-bold text-indigo-600">{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5 mb-3">
              <div
                className="bg-indigo-600 h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>

            <button className="w-full py-2 bg-gray-50 text-indigo-600 text-xs font-bold rounded-lg border border-indigo-100 hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2">
              View Units <Clock className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <>
    <div className="space-y-6">
      {/* Self-Paced Discovery Banner */}
      {isSelfPacedMode && !selectedCourse && (
        <div className="bg-gradient-to-r from-primary-600 to-indigo-700 rounded-xl p-6 text-white shadow-lg relative overflow-hidden animate-fade-in">
          <div className="absolute right-0 top-0 opacity-10 transform translate-x-1/4 -translate-y-1/4">
            <BookOpen className="w-64 h-64" />
          </div>
          <div className="relative z-10">
            <div className="flex items-center space-x-2 mb-2">
              <span className="px-2 py-0.5 bg-white bg-opacity-20 rounded-full text-xs font-bold uppercase tracking-wider border border-white border-opacity-30">
                Discovery Mode
              </span>
            </div>
            <h2 className="text-3xl font-bold mb-2">Self-Paced Learning Portal</h2>
            <p className="text-primary-100 max-w-2xl text-lg">
              Welcome to your flexible learning journey. You can browse all available courses, explore their subjects, and enroll in what interests you most—anytime, anywhere.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <div className="flex items-center space-x-2 bg-black bg-opacity-10 px-3 py-1.5 rounded-lg text-sm border border-white border-opacity-10">
                <CheckCircle className="w-4 h-4 text-green-300" />
                <span>Unlimited Access</span>
              </div>
              <div className="flex items-center space-x-2 bg-black bg-opacity-10 px-3 py-1.5 rounded-lg text-sm border border-white border-opacity-10">
                <Clock className="w-4 h-4 text-orange-300" />
                <span>Flexible Schedule</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center space-x-2">
            {selectedCourse && (
              <button
                onClick={() => setSelectedCourse(null)}
                className="p-1 hover:bg-gray-100 rounded-full text-gray-500 transition-colors"
              >
                <AlertCircle className="w-6 h-6 rotate-180" />
              </button>
            )}
            <h1 className="text-2xl font-bold text-gray-900">
              {selectedCourse ? selectedCourse.name : (isSelfPacedMode ? 'Browse Courses' : 'My Units')}
            </h1>
          </div>
          <p className="text-gray-600 mt-1">
            {selectedCourse
              ? `Exploring available units/subjects for ${selectedCourse.name}`
              : studentData?.course?.name
                ? `Course: ${studentData.course.name}`
                : isSelfPacedMode
                  ? 'Discover and enroll in available courses'
                  : 'Manage your enrolled courses and track progress'}
            {studentData?.campus?.name && !selectedCourse && ` • ${studentData.campus.name}`}
          </p>

          {/* Course Completion Status */}
          {studentData?.courseStatus && (
            <div className="mt-2">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${studentData.courseStatus === 'completed'
                ? 'bg-green-100 text-green-800'
                : studentData.courseStatus === 'in-progress'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-yellow-100 text-yellow-800'
                }`}>
                {studentData.courseStatus === 'completed' && '🎉 '}
                {studentData.courseStatus === 'in-progress' && '📚 '}
                {studentData.courseStatus === 'enrolled' && '📝 '}
                Course Status: {
                  studentData.courseStatus === 'completed' ? 'Completed' :
                    studentData.courseStatus === 'in-progress' ? 'In Progress' :
                      'Enrolled'
                }
              </span>
            </div>
          )}
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-2 mt-4 sm:mt-0">
          <button
            onClick={handleDownloadTranscript}
            disabled={downloading}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors flex items-center"
          >
            <FileText className="w-4 h-4 mr-2" />
            {downloading ? 'Processing...' : 'Download Transcript'}
          </button>
          {[
            { key: 'all', label: 'All Units', count: units.length },
            { key: 'active', label: 'Active', count: units.filter(u => u.status === 'active').length },
            { key: 'completed', label: 'Completed', count: units.filter(u => u.status === 'completed').length },
            { key: 'pending', label: 'Payment Pending', count: units.filter(u => u.paymentStatus === 'pending').length }
          ].map(({ key, label, count }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === key
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {label} ({count})
            </button>
          ))}
        </div>
      </div>




      {/* Courses or Units Grid */}
      {isSelfPacedMode && !selectedCourse ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {courses.map(course => (
            <CourseCard key={course.id} course={course} />
          ))}
        </div>
      ) : filteredUnits.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filteredUnits.map((unit) => (
            <UnitCard key={unit.id} unit={unit} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No units found</h3>
          <p className="text-gray-600">
            {filter === 'all'
              ? "You haven't enrolled in any units yet."
              : `No units match the "${filter}" filter.`
            }
          </p>
        </div>
      )}
    </div>

      {/* Payment History Modal */}
      {paymentModalUnit && (
        <PaymentHistoryModal
          unit={paymentModalUnit}
          user={user}
          onClose={() => setPaymentModalUnit(null)}
        />
      )}
    </>
  );
};

export default Units;
